import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CatalogTableC from "../../components/Catalogs/CatalogTableC";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import type {
  IFilterData,
  IHeaderCellProps,
} from "../../components/Organismos/Table/FilteredTable.types";
import { formatCalendarDateToString } from "../../utils/dates";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import { useDispatch } from "react-redux";
import { manageFirmSlice } from "../../redux";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import { MembershipStatus } from "../../Enums/FirmEnum";
import {
  ServiceType as OfficeServiceType,
  MembershipType as OfficeMembershipType,
} from "../../Enums/OfficeEnum";
import {
  ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER,
  buildAccountingTableDisplayRows,
  fetchAccountingFirmOfficeIndex,
  type AccountingFirmOfficeGroup,
  type AccountingTableRow,
} from "../../utils/accountingOfficeTableGrouping";
import { formatAccountingServiceTypeLabel } from "../../utils/accountingOfficeDetail";
import {
  resolveManageFirmMainListJurisdictionDisplay,
  resolveManageFirmMainListJurisdictionSortKey,
} from "../../utils/manageFirmOfficeFormatting";
import {
  firmLongName,
  firmShortLabel,
  type FirmNameSource,
} from "../../utils/firmDisplayName";
import { getAllFirmNames } from "../../Services/Firm";
import type { ISorting } from "../../interfaces/Components/ITable";
import { MAX_TABLE_PAGE_SIZE } from "../../constants/tablePagination";
import Stats from "../../components/Atomos/Icons/Stats";
import FirmDescriptionModal from "./FirmDescriptionModal";
import FirmLogoModal from "./FirmLogoModal";
import OfficeStatisticsModal from "./OfficeStatisticsModal";
import {
  getMembershipStatusDisplayColor,
  normalizeMembershipStatus,
} from "../../utils/membershipStatus";
import Modal from "../../components/Atomos/Modals/Modal";
import FirmLogs from "./FirmLogs";
import Button from "../../components/staff/Button";
import Plus from "../../components/Atomos/Icons/EditMode/Plus";
import { ReactComponent as WsgLogo } from "../../assets/images&icons/wsg.svg";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import { useAuth } from "../../store/AuthContext";
import { isHelpCenterStaff } from "../HelpCenter/helpCenterPermissions";
import {
  isWsgStaffProfilesFirm,
  WSG_ENTITY_BUTTON_LABEL,
} from "./wsgStaffProfiles";
import { WORLD_SERVICES_GROUP_FIRM_ID } from "../../utils/wsgStaffFirms";
import ManageFirmDashboard from "./ManageFirmDashboard";
import ManageFirmWsgOverviewPanel from "./ManageFirmWsgOverviewPanel";
import ManageFirmProfessionalProfilesPanel from "./ManageFirmProfessionalProfilesPanel";
import ManageFirmWsgContactsPanel from "./ManageFirmWsgContactsPanel";
import ManageFirmJurisdictionMembersPanel from "./ManageFirmJurisdictionMembersPanel";
import ManageFirmOfficeRowActions, {
  type ManageFirmOfficeRowActionHandlers,
} from "./ManageFirmOfficeRowActions";
import type { FirmLogOfficeScope } from "./Logs/firmLogsUtils";
import { getManageFirmOverview } from "../../Services/ManageFirmOverview";
import {
  MANAGE_FIRM_OVERVIEW_SERVICE_LABELS,
  type ManageFirmOverviewResponse,
  type ManageFirmOverviewServiceKey,
} from "../../interfaces/models/IManageFirmOverview";
import DataTableModal, {
  type DataTableModalConfig,
} from "../Events/components/ModalTable";

function formatRegionDisplay (regionNames: unknown): string {
  if (regionNames == null) return "";
  if (Array.isArray(regionNames)) {
    return regionNames
      .map((value) => String(value ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(regionNames).trim();
}

function formatMembershipTypeLabel (raw: unknown): string {
  const key = String(raw ?? "").trim();
  if (!key) return "";

  if (key in OfficeMembershipType) {
    return OfficeMembershipType[key as keyof typeof OfficeMembershipType];
  }

  const normalizedKey = Object.entries(OfficeMembershipType).find(
    ([enumKey]) => enumKey.toLowerCase() === key.toLowerCase(),
  );
  if (normalizedKey) return normalizedKey[1];

  if (key === "Multiple Primary" || key === "Primary Multiple") {
    return OfficeMembershipType.multiple;
  }

  return key;
}

function sortKeyForManageFirmOfficeRow (
  row: Record<string, unknown>,
  field: string,
): string {
  switch (field) {
    case "region":
    case "regionNames":
    case "regions":
      return String(row.region ?? row.regionNames ?? "").toLowerCase();
    case "firm":
      return String(row.firm ?? row.name ?? "").toLowerCase();
    case "shortId":
      return String(row.shortId ?? "").toLowerCase();
    case "jurisdiction":
      return String(
        row.jurisdictionSortKey ?? row.jurisdiction ?? "",
      ).toLowerCase();
    case "membershipStatus":
      return String(row.membershipStatus ?? "").toLowerCase();
    case "membershipType":
      return String(row.membershipType ?? "").toLowerCase();
    case "serviceType":
      return String(row.serviceType ?? "").toLowerCase();
    case "dateJoined":
      return String(row.dateJoined ?? "");
    default:
      return String(row[field] ?? "").toLowerCase();
  }
}

function sortOfficeRowsBeforeGrouping (
  rows: AccountingTableRow[],
  sorting: ISorting | undefined,
): AccountingTableRow[] {
  if (!sorting?.field) return rows;

  const order = sorting.order === "desc" ? -1 : 1;
  return rows.slice().sort((left, right) => {
    const cmp = sortKeyForManageFirmOfficeRow(
      left,
      sorting.field,
    ).localeCompare(
      sortKeyForManageFirmOfficeRow(right, sorting.field),
      undefined,
      { sensitivity: "base", numeric: true },
    );
    if (cmp === 0) return 0;
    return cmp * order;
  });
}

const membershipStatusOptions = Object.entries(MembershipStatus).map(
  ([key, value]) => ({
    id: key,
    name: String(value),
  }),
);
const serviceTypeOptions = Object.entries(OfficeServiceType).map(
  ([key, value]) => ({
    id: key,
    name: String(value),
  }),
);
const membershipTypeOptions = Object.entries(OfficeMembershipType).map(
  ([key, value]) => ({
    id: key,
    name: String(value),
  }),
);

const ManageFirmList: React.FC = () => {
  const navigate = useNavigate();
  const { isStaffMode } = useStaffMode();
  const { tokenDecoded } = useAuth();
  const canAccessWsgEntity = isHelpCenterStaff(
    tokenDecoded?.extension_WSG_Permissions,
  );
  const { regionData: regionList } = UseRegionList();
  const [regionData, setRegionData] = useState(
    regionList.map((r) => ({ id: r.id, name: r.name })),
  );
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [firmOfficeIndex, setFirmOfficeIndex] = useState<
    Map<string, AccountingFirmOfficeGroup>
  >(new Map());
  const [firmNameById, setFirmNameById] = useState<
    Map<string, FirmNameSource>
  >(new Map());
  const [expandedFirmIds, setExpandedFirmIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [activeSorting, setActiveSorting] = useState<ISorting>({
    field: "firm",
    order: "asc",
  });
  const [descriptionModalFirm, setDescriptionModalFirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [logoModalFirm, setLogoModalFirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [statsModalOffice, setStatsModalOffice] = useState<{
    officeId: string;
    firmId: string;
    label: string;
  } | null>(null);
  const [firmLogTarget, setFirmLogTarget] = useState<{
    firmId: string;
    name: string;
    officeScope?: FirmLogOfficeScope;
  } | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [overviewData, setOverviewData] =
    useState<ManageFirmOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [primaryJurisdictionsModal, setPrimaryJurisdictionsModal] =
    useState<DataTableModalConfig | null>(null);
  const dispatch = useDispatch();
  const endpoint = "/office";

  useEffect(() => {
    let cancelled = false;
    setOverviewLoading(true);
    void getManageFirmOverview()
      .then((data) => {
        if (!cancelled) setOverviewData(data);
      })
      .catch(() => {
        if (!cancelled) setOverviewData(null);
      })
      .finally(() => {
        if (!cancelled) setOverviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleFirmExpanded = useCallback((firmId: string) => {
    const normalized = firmId.trim();
    if (!normalized) return;
    setExpandedFirmIds((prev) => {
      const next = new Set(prev);
      if (next.has(normalized)) next.delete(normalized);
      else next.add(normalized);
      return next;
    });
  }, []);

  const openPrimaryJurisdictionsModal = useCallback(
    (serviceType: ManageFirmOverviewServiceKey) => {
      const rows =
        overviewData?.primaryJurisdictionsByServiceType?.[serviceType] ?? [];
      setPrimaryJurisdictionsModal({
        title: `Primary Jurisdictions Representation — ${MANAGE_FIRM_OVERVIEW_SERVICE_LABELS[serviceType]}`,
        columns: [
          { header: "Firm", accessorKey: "firmName" },
          { header: "Short ID", accessorKey: "shortId" },
          { header: "Jurisdiction", accessorKey: "jurisdiction" },
          { header: "Region", accessorKey: "region" },
          { header: "Membership Type", accessorKey: "membershipType" },
          { header: "Professionals", accessorKey: "professionals" },
        ],
        data: rows.map((row) => ({ ...row })),
        showExportToExcel: true,
      });
    },
    [overviewData],
  );

  const manageFirmRowActionHandlers = useMemo<ManageFirmOfficeRowActionHandlers>(
    () => ({
      onOpenStatistics: (input) => setStatsModalOffice(input),
      onOpenFirmDescription: (input) =>
        setDescriptionModalFirm({ id: input.firmId, name: input.name }),
      onOpenFirmLogo: (input) =>
        setLogoModalFirm({ id: input.firmId, name: input.name }),
      onOpenFirmLog: (input) =>
        setFirmLogTarget({
          firmId: input.firmId,
          name: input.name,
          officeScope: input.officeScope,
        }),
      onToggleFirmExpanded: toggleFirmExpanded,
    }),
    [toggleFirmExpanded],
  );

  const overviewBanner = useMemo(
    () => (
      <ManageFirmDashboard
        panels={[
          {
            id: "wsg-overview",
            fullWidth: true,
            render: () => (
              <ManageFirmWsgOverviewPanel
                data={overviewData}
                loading={overviewLoading}
                onServiceTypeClick={openPrimaryJurisdictionsModal}
              />
            ),
          },
          {
            id: "professional-profiles",
            render: () => (
              <ManageFirmProfessionalProfilesPanel
                data={overviewData?.professionalProfiles}
                loading={overviewLoading}
              />
            ),
          },
          {
            id: "wsg-contacts",
            render: () => (
              <ManageFirmWsgContactsPanel
                data={overviewData?.wsgContacts}
                loading={overviewLoading}
              />
            ),
          },
          {
            id: "jurisdiction-members",
            fullWidth: true,
            render: () => (
              <ManageFirmJurisdictionMembersPanel
                multiplePrimaryRows={
                  overviewData?.multiplePrimaryJurisdictionMembers
                }
                secondaryRows={overviewData?.secondaryJurisdictionMembers}
                loading={overviewLoading}
                actionHandlers={manageFirmRowActionHandlers}
              />
            ),
          },
        ]}
      />
    ),
    [
      overviewData,
      overviewLoading,
      openPrimaryJurisdictionsModal,
      manageFirmRowActionHandlers,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    void fetchAccountingFirmOfficeIndex().then((index) => {
      if (!cancelled) {
        setFirmOfficeIndex(index);
      }
    });
    void getAllFirmNames().then((firms) => {
      if (cancelled) return;
      setFirmNameById(
        new Map(
          firms.map((firm) => [
            firm.id,
            {
              name: firm.name,
              firmName: firm.firmName,
              shortName: firm.shortName,
            },
          ]),
        ),
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatOfficeRows = useCallback(
    (data: any[]) => {
      if (!Array.isArray(data)) return [];

      return data
        .filter((d: any) => {
          const firmId = String(d.firm ?? d.firmId ?? "").trim();
          return !isWsgStaffProfilesFirm(firmId);
        })
        .map((d: any) => {
        const _apiOffice = { ...d };
        const firmId = String(d.firm ?? "").trim();
        const firmNames = firmNameById.get(firmId);
        const nameSource: FirmNameSource = {
          name: firmNames?.name ?? d.name,
          firmName: firmNames?.firmName,
          firmLegalName: d.firmLegalName,
          shortName: firmNames?.shortName ?? d.firmShortName,
          firmShortName: d.firmShortName,
        };
        const displayName = firmShortLabel(nameSource);
        const _apiFirm = {
          id: firmId,
          name: firmNames?.name || firmLongName(nameSource) || displayName,
          firmName: firmNames?.firmName ?? d.firmLegalName ?? d.firmName,
          shortId: d.shortId,
          membershipStatus: d.membershipStatus,
          serviceType: d.serviceType,
          jurisdictionName: d.jurisdictionName,
          regionNames: d.regionNames,
          createdDate: d.createdDate,
        };

        const jurisdictionInput = {
          countryName: d.countryName,
          stateName: d.stateName,
          jurisdiction: d.jurisdiction,
          jurisdictionName: d.jurisdictionName,
        };
        const jurisdictionDisplay =
          resolveManageFirmMainListJurisdictionDisplay(jurisdictionInput);

        return {
          ...d,
          _apiOffice,
          _apiFirm,
          firmId,
          shortId: d.shortId ?? d.legacyId ?? "",
          firm: displayName,
          name: displayName,
          jurisdiction: jurisdictionDisplay.label,
          jurisdictionSortKey:
            resolveManageFirmMainListJurisdictionSortKey(jurisdictionInput),
          _jurisdictionDisplay: jurisdictionDisplay,
          region: formatRegionDisplay(d.regionNames ?? d.regionName),
          dateJoined: d.dateJoined
            ? formatCalendarDateToString(d.dateJoined)
            : "",
          membershipType: formatMembershipTypeLabel(d.membershipType),
          membershipStatus: normalizeMembershipStatus(d.membershipStatus) || "",
          serviceType: formatAccountingServiceTypeLabel(d.serviceType) || "",
        };
      });
    },
    [firmNameById],
  );

  const formatDataGrouped = useCallback(
    (data: any[]) => {
      const formatted = formatOfficeRows(data) as AccountingTableRow[];
      const sorted = sortOfficeRowsBeforeGrouping(formatted, activeSorting);
      return buildAccountingTableDisplayRows(
        sorted,
        firmOfficeIndex,
        expandedFirmIds,
      );
    },
    [formatOfficeRows, firmOfficeIndex, expandedFirmIds, activeSorting],
  );

  const manageFirmActionsCell = useCallback(
    ({ row }: IHeaderCellProps) => {
      const isFirmRow =
        row._rowKind === "standalone" || row._rowKind === "groupParent";
      const isChildRow = row._rowKind === "groupChild";
      const officeId = String(row.id ?? "").trim();
      const firmId = String(row.firmId ?? row._displayFirmId ?? "").trim();
      const hasExpand = Boolean(row._hasChildren && row._displayFirmId);
      const expandFirmId = String(row._displayFirmId ?? "");
      const firmName = String(
        row.firm ?? row.name ?? row.firmName ?? "",
      ).trim();
      const jurisdiction = String(row.jurisdiction ?? "").trim();
      const officeScope: FirmLogOfficeScope | undefined = isChildRow && officeId
        ? { kind: "office", officeId }
        : row._rowKind === "standalone" && officeId
          ? { kind: "standalone", officeId }
          : isFirmRow
            ? { kind: "parent" }
            : undefined;
      const logModalTitle =
        isChildRow && jurisdiction
          ? `${firmName} — ${jurisdiction}`
          : firmName;

      return (
        <ManageFirmOfficeRowActions
          officeId={officeId}
          firmId={firmId}
          firmName={logModalTitle}
          jurisdiction={jurisdiction}
          showFirmActions={isFirmRow}
          showOfficeLog={isChildRow}
          officeScope={officeScope}
          expandFirmId={hasExpand ? expandFirmId : ""}
          isExpanded={expandedFirmIds.has(expandFirmId)}
          handlers={manageFirmRowActionHandlers}
        />
      );
    },
    [expandedFirmIds, manageFirmRowActionHandlers],
  );

  const manageFirmNameCell = useCallback(
    ({ row, value }: IHeaderCellProps) => {
      const firmId = String(row.firmId ?? "").trim();
      const isChild = row._rowKind === "groupChild";
      const isParent = row._rowKind === "groupParent";

      return (
        <div className="min-w-0">
          <div className={isParent ? "font-semibold" : ""}>
            {isChild ? (
              <span className="mr-1 text-gray-font" aria-hidden="true">
                └
              </span>
            ) : null}
            <PersistentLink
              className="edit text-secondary underline"
              to={`Dashboard/${firmId}`}
              onClick={() => {
                dispatch(manageFirmSlice.actions.SetFirm(row._apiFirm ?? row));
              }}
            >
              {value}
            </PersistentLink>
          </div>
        </div>
      );
    },
    [dispatch],
  );

  const manageFirmJurisdictionCell = useCallback(({ row }: IHeaderCellProps) => {
    const display = row._jurisdictionDisplay as
      | { label?: string; indent?: boolean }
      | undefined;
    const label = String(display?.label ?? row.jurisdiction ?? "").trim();
    if (!label) return "";

    return (
      <span className={display?.indent ? "pl-4" : undefined}>{label}</span>
    );
  }, []);

  const membershipStatusCell = useCallback(({ value }: IHeaderCellProps) => {
    const label = normalizeMembershipStatus(String(value ?? ""));
    const color = getMembershipStatusDisplayColor(label);
    return <span style={{ color: color || undefined }}>{label}</span>;
  }, []);

  const serviceTypeCell = useCallback(({ value }: IHeaderCellProps) => {
    return <span>{formatAccountingServiceTypeLabel(value) || ""}</span>;
  }, []);

  const columnsDef = useMemo(
    () => [
      {
        id: "actions",
        header: "Action",
        width: 172,
        enableSorting: false,
        columnOrderFixed: true,
        defaultVisibility: true,
        cell: manageFirmActionsCell,
      },
      { id: "shortId", header: "Short ID", columnOrderFixed: true },
      {
        id: "firm",
        header: "Firm Name",
        width: 180,
        columnOrderFixed: true,
        cell: manageFirmNameCell,
      },
      { id: "region", header: "Region" },
      {
        id: "jurisdiction",
        header: "Jurisdiction",
        cell: manageFirmJurisdictionCell,
      },
      { id: "dateJoined", header: "Joined" },
      { id: "membershipType", header: "Type" },
      {
        id: "membershipStatus",
        header: "Status",
        cell: membershipStatusCell,
      },
      { id: "serviceType", header: "Service Type", cell: serviceTypeCell },
    ],
    [
      manageFirmActionsCell,
      manageFirmNameCell,
      manageFirmJurisdictionCell,
      membershipStatusCell,
      serviceTypeCell,
    ],
  );

  useEffect(() => {
    setRegionData(regionList?.map((r) => ({ id: r.id, name: r.name })));
  }, [regionList]);

  const firmList = useMemo(() => {
    const options: { id: string; name: string }[] = [];
    for (const [firmId, group] of firmOfficeIndex) {
      if (isWsgStaffProfilesFirm(firmId)) continue;
      const office = group.offices[0];
      const firmNames = firmNameById.get(firmId);
      const name = firmShortLabel({
        name: firmNames?.name ?? office?.name,
        shortName: firmNames?.shortName ?? office?.firmShortName,
        firmShortName: office?.firmShortName,
      });
      options.push({ id: firmId, name: name || firmId });
    }
    return options.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }, [firmOfficeIndex, firmNameById]);

  useEffect(() => {
    localStorage.removeItem("firmCountWorldwide");
    localStorage.removeItem("firmCountPrimary");
    dispatch(manageFirmSlice.actions.SetFirm(undefined));
  }, [dispatch]);

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Firm",
        fieldName: "firm",
        objectData: firmList || [],
        comparison: "EQUAL",
      },
      {
        name: "Membership Type",
        fieldName: "membershipType",
        objectData: membershipTypeOptions,
        comparison: "IN",
      },
      {
        name: "Region",
        fieldName: "regions",
        objectData: regionData || [],
        comparison: "BY_ID",
      },
      {
        name: "Service",
        fieldName: "serviceType",
        objectData: serviceTypeOptions,
        comparison: "IN",
      },
      {
        name: "Status",
        fieldName: "membershipStatus",
        objectData: membershipStatusOptions,
        comparison: "BY_NAME",
      },
    ],
    [firmList, regionData],
  );

  const persistentFilter = useMemo(
    () => ({ ...ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER }),
    [],
  );

  return (
    <>
      <DataTableModal
        config={primaryJurisdictionsModal}
        onClose={() => setPrimaryJurisdictionsModal(null)}
      />
      <Modal
        isModalOpen={firmLogTarget != null}
        toggleModal={() => setFirmLogTarget(null)}
        title={firmLogTarget?.name ?? "Firm logs"}
        titleColor="text-accent-Default-dark font-decimal text-2xl font-medium"
        className="w-full px-10 pt-10 pb-6"
        bodyClassName="!flex-1 min-h-0 !flex !w-full !flex-col !overflow-hidden !p-0 !pt-2"
        compact
        compactBodyScrolls
        outerPaddingOnly
      >
        {firmLogTarget ? (
          <FirmLogs
            key={`${firmLogTarget.firmId}-${firmLogTarget.officeScope?.kind ?? "parent"}-${firmLogTarget.officeScope &&
                firmLogTarget.officeScope.kind !== "parent"
                ? firmLogTarget.officeScope.officeId
                : ""
              }`}
            firmId={firmLogTarget.firmId}
            firmName={firmLogTarget.name}
            officeScope={firmLogTarget.officeScope}
            embeddedInScrollContainer
          />
        ) : null}
      </Modal>
      <CatalogTableC
        catalogName={"FIRMS"}
        route={"Firm"}
        catalogShortName={"FIRM"}
        totalCount={totalCount}
        noNewCatalog
        contentClassName="!p-0"
        rightActions={
          <div className="flex items-center p-2">
            <Button
              text={showStats ? "Hide Stats" : "Show Stats"}
              color="white"
              variant="outlined"
              size="fixed"
              onClick={() => setShowStats((prev) => !prev)}
              icon={<Stats />}
            />
          </div>
        }
      >
        {descriptionModalFirm ? (
          <FirmDescriptionModal
            isOpen={Boolean(descriptionModalFirm)}
            firmId={descriptionModalFirm.id}
            firmName={descriptionModalFirm.name}
            onClose={() => setDescriptionModalFirm(null)}
          />
        ) : null}
        {logoModalFirm ? (
          <FirmLogoModal
            isOpen
            firmId={logoModalFirm.id}
            firmName={logoModalFirm.name}
            onClose={() => setLogoModalFirm(null)}
          />
        ) : null}
        {statsModalOffice ? (
          <OfficeStatisticsModal
            isOpen
            officeId={statsModalOffice.officeId}
            firmId={statsModalOffice.firmId}
            officeLabel={statsModalOffice.label}
            firmOfficeIndex={firmOfficeIndex}
            onClose={() => setStatsModalOffice(null)}
          />
        ) : null}
        <FilteredTableCore
          tableKey="/ManageFirm"
          bannerContent={showStats ? overviewBanner : undefined}
          columnsDef={columnsDef}
          showRowNumber
          showTopHorizontalScrollBar
          endpoint={endpoint}
          filtersData={filtersData}
          persistentFilter={persistentFilter}
          formatData={formatDataGrouped}
          formatExcelData={formatOfficeRows}
          defaultSorting={{ field: "firm", order: "asc" }}
          onSortingChange={setActiveSorting}
          defaultPageSize={MAX_TABLE_PAGE_SIZE}
          onTotalCountChange={setTotalCount}
          showSearchInput
          searchPlaceholder="Search by Firm Name, Short ID, Jurisdiction or Region"
          searchToolbarAccessory={
            <div className="flex flex-wrap items-center gap-4">
              <Button
                text="Create Firm"
                color={isStaffMode ? "secondary" : "primary"}
                variant="filled"
                size="fixed"
                onClick={() => navigate("/ManageFirm/Create")}
                icon={<Plus />}
              />
              {canAccessWsgEntity ? (
                <Button
                  text={WSG_ENTITY_BUTTON_LABEL}
                  color={isStaffMode ? "secondary" : "primary"}
                  variant="outlined"
                  size="fixed"
                  onClick={() =>
                    navigate(
                      `/ManageFirm/Professionals/${WORLD_SERVICES_GROUP_FIRM_ID}`,
                    )
                  }
                  icon={
                    <WsgLogo
                      className="h-5 w-5 text-current [&_path]:![fill:currentColor]"
                      aria-hidden="true"
                    />
                  }
                />
              ) : null}
            </div>
          }
          excelFilename="manage_firms"
        />
      </CatalogTableC>
    </>
  );
};

export default ManageFirmList;
